import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  FirebaseAnalyticsService,
  FileUploadService,
  UploadPatientModel,
  UploadServiceFileItem,
  VideoUploadState,
} from "@modules/core";
import { CeloFileUploader, UploadItem } from "@modules/shared/file-upload";

@Component({
  selector: "app-file-upload-preview",
  templateUrl: "./file-upload-preview.component.html",
  styleUrls: ["./file-upload-preview.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FileUploadPreviewComponent implements OnInit, OnChanges {
  @Output() closeMe = new EventEmitter();
  @Output() public items: EventEmitter<UploadServiceFileItem[]> =
    new EventEmitter<UploadServiceFileItem[]>();

  @Output() public uploaded: EventEmitter<void> = new EventEmitter<void>();

  @Input() noUpload: boolean;
  @Input() type: string;
  @Input() isInSecureLibrary: string;
  pageName: string;
  @Input() uploader: CeloFileUploader;
  @Input() refreshCount: number;
  patientValidity = true;

  @Input() public patient: UploadPatientModel = {
    uid: "",
    firstName: "",
    lastName: "",
    dateOfBirth: null,
    gender: null,
  };

  private _selectedItemIndex: number = 0;
  @Input() public get selectedItemIndex(): number {
    return this._selectedItemIndex;
  }

  public set selectedItemIndex(index: number) {
    this._selectedItemIndex = index;
    this.selectedItemIndexChange.emit(index);
  }

  @Output() public selectedItemIndexChange: EventEmitter<number> =
    new EventEmitter<number>();

  // hasPatient:boolean;
  dropZoneActive: string;

  // Preview UI state kept out of the upload items, keyed by item id.
  private previews = new Map<
    string,
    { picUrl?: string; pdfData?: Uint8Array }
  >();

  picUrlFor(item: UploadItem): string | undefined {
    return (
      this.previews.get(item.id)?.picUrl ??
      this.uploadService.videoStateFor(item)?.thumbnailUrl
    );
  }

  isVideo(item: UploadItem): boolean {
    return this.uploadService.isVideo(item);
  }

  videoStateFor(item: UploadItem): VideoUploadState | undefined {
    return this.uploadService.videoStateFor(item);
  }

  get isConvertingVideo(): boolean {
    return this.uploadService.isConvertingVideo;
  }

  pdfDataFor(item: UploadItem): Uint8Array | undefined {
    return this.previews.get(item.id)?.pdfData;
  }

  constructor(
    private uploadService: FileUploadService,
    public analyticsService: FirebaseAnalyticsService
  ) {}

  ngOnInit() {
    this.refresh();
  }

  @HostListener("window:keydown", ["$event"])
  public keyboardInput(event: KeyboardEvent) {
    if (event.code === "ArrowRight") {
      this.onNext(1);
    } else if (event.code === "ArrowLeft") {
      this.onNext(-1);
    }
  }

  onNext(direction) {
    this.selectedItemIndex += direction;
    if (this.selectedItemIndex >= this.uploader.queue.length) {
      this.selectedItemIndex = this.uploader.queue.length - 1;
    } else if (this.selectedItemIndex < 0) {
      this.selectedItemIndex = 0;
    }
  }

  dropZoneState(state: boolean) {
    this.dropZoneActive = state ? "valid" : "";
  }

  scrollTo(el: HTMLElement) {
    setTimeout(() => {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }, 100);
  }

  upload() {
    this.uploaded.emit();
    if (this.noUpload) {
      this.closeMe.emit();
      return;
    }
    this.uploadService.patient = this.patient;
    this.uploadService.upload();
    this.raiseEventOnUploadButtonClick();
  }

  raiseEventOnUploadButtonClick() {
    this.analyticsService.raiseEvent("button_click", {
      flow: this.isInSecureLibrary ? "secure_library" : "message",
      button_id: this.isInSecureLibrary ? "save_media" : "save_and_send_media",
      number_of_files: this.uploader.queue.length,
    });
  }

  setpageName() {
    if (!this.uploader || !this.uploader.queue) {
      return this.type;
    }
    let pageName = "";
    let photosCount = 0;
    let documentsCount = 0;
    this.uploader.queue.forEach((element) => {
      if (
        element.file.type.startsWith("image/") ||
        this.uploadService.isVideo(element)
      ) {
        // type = 'Photos';
        photosCount++;
      } else if (element.file.type.startsWith("application/pdf")) {
        // type = 'Documents';
        documentsCount++;
      }
    });
    if (photosCount && !documentsCount) {
      pageName = "Media";
    } else if (!photosCount && documentsCount) {
      pageName = "Documents";
    } else if (photosCount && documentsCount) {
      pageName = "Files";
    }
    return pageName;
  }

  refresh() {
    if (!this.uploader || !this.uploader.queue) {
      return;
    }
    this.setPreviews(this.uploader.queue);
    this.pageName = this.setpageName();
    this.items.emit([...this.uploader.queue] as UploadServiceFileItem[]);
  }

  handleNewFiles() {
    this.refresh();
  }

  ngOnChanges() {
    this.pageName = this.setpageName();
  }

  setPreviews(files) {
    for (let i = 0; i < files.length; i++) {
      if (this.uploadService.isPhoto(files[i])) {
        this.readURL(files[i]);
      } else if (this.uploadService.isDocument(files[i])) {
        this.loadDocumentData(files[i]);
      }
    }
  }

  readURL(item: UploadItem) {
    if (!item.file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      this.setPreview(item.id, { picUrl: e.target?.result as string });
    };
    reader.readAsDataURL(item.file);
  }

  private loadDocumentData(item: UploadItem) {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e || !e.target) {
        return;
      }
      this.setPreview(item.id, {
        pdfData: new Uint8Array(e.target.result as ArrayBuffer),
      });
    };
    reader.readAsArrayBuffer(item.file);
  }

  private setPreview(
    id: string,
    patch: { picUrl?: string; pdfData?: Uint8Array }
  ) {
    this.previews.set(id, { ...this.previews.get(id), ...patch });
  }

  // togglePatient(){
  //   this.hasPatient=!this.hasPatient;
  //   if(!this.hasPatient){
  //     this.patient={};
  //   }
  // }

  removeFile(index: number) {
    const item = this.uploader.queue[index];
    if (item) {
      this.previews.delete(item.id);
      this.uploader.remove(item);
    }
    if (this.selectedItemIndex >= this.uploader.queue.length) {
      this.selectedItemIndex--;
    }
    this.closeIfEmpty();
    this.refresh();
  }

  closeIfEmpty() {
    if (this.uploader.queue.length == 0) {
      this.closeMe.emit();
    }
  }
}
